import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
  SimpleChanges,
  ChangeDetectorRef,
  OnDestroy,
  WritableSignal,
  signal,
  AfterViewInit,
} from '@angular/core';
import { logViewItem } from '@app/core/services/google-analytics.lazy';
import { AkitaScreenQuery } from '@app/akita/screen/state/screen.query';
import { ScreenSizeInfoState } from '@app/akita/screen/state/screen.store';
import { PriceModel } from '@app/shared/models/api/price.model';
import { BehaviorSubject, Observable, Subject, map } from 'rxjs';
import { GoogleAnalyticsService } from '@app/core/services/google-analytics.service';
import { Product } from '@app/akita/api/products/models/product.model';
import { OmnisendService } from '@app/shared/services/omnisend.service';
import { AmountPipe } from '../../../shared/pipes/amount.pipe';
// eslint-disable-next-line max-len
import { PremiumShippingEstimateCardComponent } from '../../../checkout/components/premium-shipping-estimate-card/premium-shipping-estimate-card.component';
import { ShippingEstimateCardComponent } from '../../../checkout/components/shipping-estimate-card/shipping-estimate-card.component';
import { FeaturedBuyButtonsComponent } from '../featured-buy-buttons/featured-buy-buttons.component';
import { FeaturedDetailsCardComponent } from '../featured-details-card/featured-details-card.component';
import { PopsySkeletonComponent } from '../../../components/skeleton/popsy-skeleton.component';
import { CheckBadgeIconComponent } from '../../../shared/components/icons/generated/check-badge/check-badge.component';
import { AsyncPipe } from '@angular/common';
import { TranslocoModule } from '@ngneat/transloco';
import { AkitaRouterQuery } from '@app/akita/router/state/router.query';
import { Params } from '@angular/router';
import { InstallmentAmountPipe } from '@app/shared/pipes/installment-amount.pipe';
// eslint-disable-next-line max-len
import { ProductInstallmentOptionsComponent } from '@app/products/components/product-installment-options/product-installment-options.component';
import { TranslateColorPipe } from '@app/shared/pipes/translate-color.pipe';

@Component({
  selector: 'app-featured-purchase-info-card',
  templateUrl: './featured-purchase-info-card.template.html',
  styleUrls: ['./featured-purchase-info-card.style.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    TranslocoModule,
    CheckBadgeIconComponent,
    PopsySkeletonComponent,
    FeaturedDetailsCardComponent,
    FeaturedBuyButtonsComponent,
    ShippingEstimateCardComponent,
    PremiumShippingEstimateCardComponent,
    AsyncPipe,
    AmountPipe,
    InstallmentAmountPipe,
    ProductInstallmentOptionsComponent,
    TranslateColorPipe,
  ],
})
export class FeaturedPurchaseInfoCardComponent
  implements OnChanges, AfterViewInit, OnDestroy
{
  @Input() public product?: Product | null;
  @Input() public currency?: string | null;

  public readonly biggerThan$: Observable<ScreenSizeInfoState>;
  public readonly isBrowser: boolean;

  public readonly userReviews? = 0;
  private readonly priceSubject: BehaviorSubject<PriceModel | null>;
  public readonly price$: Observable<PriceModel | null>;

  public readonly instalmentsCount: number = 4;
  public readonly instalmentPrice: WritableSignal<PriceModel | null>;

  public testProductPage$: Observable<string | null>;

  private readonly origPriceSubject: BehaviorSubject<PriceModel | null>;
  public readonly origPrice$: Observable<PriceModel | null>;
  public hasDiscount = false;

  public showSocialShareOverlay?: boolean | null;

  private readonly destroySubject: Subject<void>;

  constructor(
    private readonly changeDetector: ChangeDetectorRef,
    private readonly analytics: GoogleAnalyticsService,
    private readonly akitaScreenQuery: AkitaScreenQuery,
    private readonly akitaRouterQuery: AkitaRouterQuery,
    private readonly omnisendService: OmnisendService
  ) {
    this.destroySubject = new Subject();
    this.priceSubject = new BehaviorSubject(null as any);
    this.price$ = this.priceSubject.asObservable();
    this.isBrowser = this.akitaRouterQuery.isBrowser;

    this.biggerThan$ = this.akitaScreenQuery.selectBiggerThan();
    this.testProductPage$ = this.akitaRouterQuery.selectParamsAndQueryParams().pipe(
      map((params: Params): string | null => {
        // testing page
        if (params.t) {
          return params.t;
        }
        return null;
      })
    );

    this.origPriceSubject = new BehaviorSubject(null as any);
    this.origPrice$ = this.origPriceSubject.asObservable();

    this.instalmentPrice = signal(null);
  }

  public ngAfterViewInit(): void {
    if (this.product) {
      logViewItem(
        this.analytics.logEventWrapper,
        this.analytics.firebaseRecordProductPageViewADConversion,
        this.analytics.facebookTrackViewItem,
        this.product
      );
      this.omnisendService.trackProductView(this.product);
    }
  }

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.currency || changes.product) {
      this.priceSubject.next(
        PriceModel.fromJson({
          amount:
            (this.product?.price?.amount || 0) +
            (this.product?.selectedInsurance?.price.amount || 0),
          currency: this.currency || 'USD',
        })
      );

      this.instalmentPrice.set({
        amount: (this.priceSubject.value?.amount || 0) / this.instalmentsCount,
        currency: this.currency || 'SAR',
      });
    }

    if (changes.currency) {
      const originalPrice = Number(`${this.product?.originalPrice.amount || 0}`);
      const productPrice = this.product?.price?.amount || 0;
      if (originalPrice && productPrice && originalPrice > productPrice) {
        this.hasDiscount = true;
        this.origPriceSubject.next(
          PriceModel.fromJson({
            amount: originalPrice,
            currency: this.currency || 'USD',
          })
        );
      } else {
        this.origPriceSubject.next(null);
      }
    }

    this.changeDetector.markForCheck();
  }

  public getTransLabel(color?: string | null): string {
    if (color) {
      color = color.replace(/[)-]/gi, '_').replace(/[(-]/gi, ''); // get rid of '(' ')'
      color = color.replace(/ /gi, '_'); // replace space with underscore
      return `label_phone_color_${color}`.toLowerCase();
    }
    return '';
  }

  public ngOnDestroy(): void {
    this.destroySubject.next();
    this.destroySubject.complete();
  }
}
